/**
 * /config/portal: os sites do tenant com o Portal Kit, a um olhar.
 *
 * Cada site mostra as colunas ativas, a versão do plugin, a última sincronização
 * e o estado (saudável, atenção, crítico) com o motivo escrito.
 */
import { Link } from 'react-router-dom';

import { Button, classesDeBotao } from '@/components/atoms';
import { EstadoDoSite } from '@/components/Portal/EstadoDoSite';
import { PortalGate } from '@/components/Portal/PortalGate';
import { useAgora } from '@/hooks/useAgora';
import { usePortalSites } from '@/hooks/usePortal';
import { formatarDataHora } from '@/i18n/formato';
import { classificarFalha } from '@/services/portal';
import { COLUNAS, type PortalSiteResumo } from '@/types/portal';
import { descreverIdade, rotuloDaColuna, segundosDesde } from '@/utils/portal';

import styles from '@/components/Portal/Portal.module.css';

function CartaoDoSite({ site, agora }: { site: PortalSiteResumo; agora: number }) {
  const inst = site.instalacao;
  const ativas = site.colunas_ativas;
  return (
    <li className={styles.site}>
      <div className={styles.siteCabeca}>
        <div>
          <h2 className={styles.siteNome}>{site.nome}</h2>
          <span className={styles.mono}>{site.url}</span>
        </div>
        <Link
          to={`/config/portal/${site.site_id}`}
          className={classesDeBotao('secondary', 'sm')}
          aria-label={`Configurar ${site.nome}`}
        >
          Configurar
        </Link>
      </div>
      <EstadoDoSite site={site} agora={agora} />
      <dl className={styles.dados}>
        <dt>Colunas ativas</dt>
        <dd>
          {ativas.length} de {COLUNAS.length}
          {ativas.length > 0 && (
            <span className={styles.mudo}> · {ativas.map(rotuloDaColuna).join(', ')}</span>
          )}
        </dd>
        <dt>Servidas pelo plugin</dt>
        <dd>
          {site.colunas_no_plugin.length > 0
            ? site.colunas_no_plugin.map(rotuloDaColuna).join(', ')
            : 'nenhuma (modo sombra)'}
        </dd>
        <dt>Plugin</dt>
        <dd>
          {inst ? `${inst.versao} instalado` : 'não instalado ou sem contato'} · versão atual{' '}
          {site.plugin.versao_atual}
        </dd>
        <dt>Última sincronização</dt>
        <dd>
          {inst
            ? `${descreverIdade(segundosDesde(inst.ultima_leitura_em, agora))} (${formatarDataHora(
                inst.ultima_leitura_em,
                {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                },
              )})`
            : 'nunca'}
        </dd>
      </dl>
    </li>
  );
}

function Lista() {
  const { data, isLoading, isError, error, refetch } = usePortalSites();
  const agora = useAgora();

  if (isLoading) return <p className={styles.mudo}>Carregando os sites…</p>;

  if (isError) {
    const tipo = classificarFalha(error);
    if (tipo === 'sem_api') {
      return (
        <div className={styles.avisoWarn} role="alert">
          <span className={styles.avisoTitulo}>
            A API do Portal Kit não está instalada nesta instância
          </span>
          <span>
            A rota <span className={styles.mono}>/config/portal</span> não respondeu. A tela aparece
            quando o servidor da plataforma for atualizado.
          </span>
        </div>
      );
    }
    if (tipo === 'sem_plano') {
      return (
        <div className={styles.avisoWarn} role="alert">
          <span className={styles.avisoTitulo}>Portal Kit não incluído no plano</span>
          <span>O servidor recusou o acesso: a feature portal_kit não está neste plano.</span>
        </div>
      );
    }
    return (
      <div className={styles.avisoCrit} role="alert">
        <span className={styles.avisoTitulo}>Não foi possível carregar os sites</span>
        <div>
          <Button type="button" size="sm" variant="secondary" onClick={() => void refetch()}>
            Tentar de novo
          </Button>
        </div>
      </div>
    );
  }

  const sites = data ?? [];
  if (sites.length === 0) {
    return (
      <p className={styles.vazio}>
        Nenhum site WordPress cadastrado. Cadastre o site em{' '}
        <Link to="/config/sites">Configurações, Sites WordPress</Link> e volte aqui.
      </p>
    );
  }
  return (
    <ul className={styles.sites} aria-label="Sites com o Portal Kit">
      {sites.map((s) => (
        <CartaoDoSite key={s.site_id} site={s} agora={agora} />
      ))}
    </ul>
  );
}

export function ConfigPortal() {
  return (
    <div className={styles.pagina}>
      <div className={styles.cabecalho}>
        <div>
          <p className={styles.migalhas}>
            <Link to="/config">Configurações</Link> / Portal
          </p>
          <h1 className={styles.titulo}>Portal Kit</h1>
          <p className={styles.sub}>
            Tudo o que um site WordPress mostra do WIREDESK se configura aqui: quais colunas estão
            ligadas, as cidades, praças e ativos de cada uma, a marca e o rodapé. O site só instala
            o plugin e lê.
          </p>
        </div>
      </div>
      <PortalGate>
        <Lista />
      </PortalGate>
    </div>
  );
}
